import { useTranslation } from "react-i18next";
import { Shield, Lock, CheckCircle } from "lucide-react";
import FadeInUp from "./FadeInUp";

const STATS = [
  { value: "Actif",  label: { fr: "En production",     en: "In production",      ar: "في الإنتاج"         }, pulse: true  },
  { value: "560",    label: { fr: "Employés gérés",    en: "Employees managed",  ar: "موظف تحت الإدارة"   }, pulse: false },
  { value: "99.9%",  label: { fr: "Disponibilité SLA", en: "SLA uptime",         ar: "وقت التشغيل SLA"    }, pulse: false },
  { value: "5",      label: { fr: "Pays couverts",     en: "Countries covered",  ar: "دول مغطاة"          }, pulse: false },
];

const BADGES = [
  {
    icon: Shield,
    title: { fr: "Conformité Multi-Pays EMEA", en: "EMEA Multi-Country Compliance", ar: "الامتثال متعدد الدول في EMEA" },
    body:  { fr: "Calculs de paie et règles RH adaptés aux législations locales : Tunisie, Qatar, Maroc, France, et plus.", en: "Payroll calculations and HR rules adapted to local legislation: Tunisia, Qatar, Morocco, France, and more.", ar: "حسابات الرواتب وقواعد الموارد البشرية المتوافقة مع التشريعات المحلية: تونس وقطر والمغرب وفرنسا وغيرها." },
  },
  {
    icon: Lock,
    title: { fr: "Données chiffrées HTTPS/TLS", en: "HTTPS/TLS Encrypted Data", ar: "بيانات مشفّرة HTTPS/TLS" },
    body:  { fr: "Toutes les communications sont chiffrées. Mots de passe hachés avec Bcrypt.", en: "All communications are encrypted. Passwords hashed with Bcrypt.", ar: "جميع الاتصالات مشفّرة. كلمات المرور مجزّأة باستخدام Bcrypt." },
  },
  {
    icon: CheckCircle,
    title: { fr: "Contrôle d'accès par rôle", en: "Role-Based Access Control", ar: "التحكم في الوصول القائم على الأدوار" },
    body:  { fr: "67+ permissions granulaires, 5 rôles système, audit complet de chaque action.", en: "67+ granular permissions, 5 system roles, full audit of every action.", ar: "أكثر من 67 صلاحية دقيقة، و5 أدوار نظام، وتدقيق كامل لكل إجراء." },
  },
];

const PulseDot = () => (
  <>
    <style>{`
      @keyframes mw-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%       { opacity: 0.5; transform: scale(1.4); }
      }
      .mw-pulse-dot { animation: mw-pulse 2s ease-in-out infinite; }
    `}</style>
    <span
      className="mw-pulse-dot"
      style={{
        display: "inline-block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: "#27a644",
        boxShadow: "0 0 6px #27a644",
        marginRight: "6px",
        verticalAlign: "middle",
      }}
    />
  </>
);

const LandingTrustSecurity = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const t = (obj) => obj[lang] || obj.fr;

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Stats row */}
        <FadeInUp>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {STATS.map((s, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#0f1011",
                  border: "1px solid #23252a",
                  borderRadius: "12px",
                  padding: "24px",
                  textAlign: "center",
                }}
              >
                <div style={{ color: "#f7f8f8", fontSize: "clamp(28px, 4vw, 36px)", fontWeight: 600, letterSpacing: "-1px", marginBottom: "4px", lineHeight: 1.2 }}>
                  {s.pulse ? (
                    <span style={{ fontSize: "22px" }}>
                      <PulseDot />{s.value}
                    </span>
                  ) : s.value}
                </div>
                <div style={{ color: "#8a8f98", fontSize: "13px" }}>{t(s.label)}</div>
              </div>
            ))}
          </div>
        </FadeInUp>

        {/* Security badges */}
        <FadeInUp>
          <div style={{ textAlign: "center", maxWidth: "480px", margin: "0 auto 48px" }}>
            <span
              style={{
                display: "inline-block",
                fontSize: "12px",
                fontWeight: 500,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                padding: "4px 14px",
                borderRadius: "9999px",
                color: "#5e6ad2",
                backgroundColor: "rgba(94,106,210,0.10)",
                border: "1px solid rgba(94,106,210,0.20)",
                marginBottom: "20px",
              }}
            >
              {t({ fr: "Sécurité & Conformité", en: "Security & Compliance", ar: "الأمن والامتثال" })}
            </span>
            <h2
              style={{
                color: "#f7f8f8",
                fontSize: "clamp(24px, 3vw, 32px)",
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "-0.8px",
              }}
            >
              {t({ fr: "Conçu pour la conformité et la sécurité", en: "Built for compliance and security", ar: "مصمّم للامتثال والأمن" })}
            </h2>
          </div>
        </FadeInUp>

        <div className="grid md:grid-cols-3 gap-4">
          {BADGES.map((badge, i) => {
            const Icon = badge.icon;
            return (
              <FadeInUp key={i} delay={i * 0.07}>
                <div
                  style={{
                    position: "relative",
                    background: "rgba(15,16,17,0.6)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(94,106,210,0.12)",
                    borderRadius: "12px",
                    padding: "24px",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                    overflow: "hidden",
                  }}
                >
                  {/* Dot grid watermark */}
                  <div style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "12px",
                    backgroundImage: "radial-gradient(circle, rgba(94,106,210,0.04) 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                    pointerEvents: "none",
                  }} />

                  <div
                    style={{
                      position: "relative",
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: "rgba(94,106,210,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "14px",
                    }}
                  >
                    <Icon style={{ color: "#5e6ad2", width: "18px", height: "18px" }} />
                  </div>
                  <div style={{ position: "relative", color: "#f7f8f8", fontSize: "15px", fontWeight: 500, letterSpacing: "-0.3px", marginBottom: "8px" }}>
                    {t(badge.title)}
                  </div>
                  <p style={{ position: "relative", color: "#8a8f98", fontSize: "14px", lineHeight: 1.5 }}>
                    {t(badge.body)}
                  </p>
                </div>
              </FadeInUp>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LandingTrustSecurity;
